import { ApiError, api } from '../api/client';
import { renderLogoMark } from './logo';
import { openRunBuilder } from './run-builder';
import { mixer } from '../audio/mixer';
import { captainArt, shipArt } from './art';
import { checkTurnNotifications, requestNotificationPermission, notificationsPermission, notificationsEnabled, setNotificationsEnabled } from './notifications';
import { isTelemetryEnabled, setTelemetryEnabled } from '../telemetry';
let lobbyPollHandle = null;
let lobbyVisibilityHandler = null;
export function unmountLobby() {
    if (lobbyPollHandle !== null)
        clearTimeout(lobbyPollHandle);
    lobbyPollHandle = null;
    if (lobbyVisibilityHandler)
        document.removeEventListener('visibilitychange', lobbyVisibilityHandler);
    lobbyVisibilityHandler = null;
}
export function renderLobby(root, me, goToMatch, signOut, goToFriends, goToProfile, goToChallenges, goToCollection) {
    unmountLobby();
    root.innerHTML = '<div class="lobby-loading">Loading the deck…</div>';
    loadAndRender();
    lobbyPollHandle = window.setInterval(() => {
        if (!document.hidden)
            loadAndRender(true);
    }, 5000);
    lobbyVisibilityHandler = () => {
        if (!document.hidden)
            loadAndRender(true);
    };
    document.addEventListener('visibilitychange', lobbyVisibilityHandler);
    let lastHash = '';
    let showFinished = false;
    async function loadAndRender(silent = false) {
        let matches = [];
        let pendingIncoming = 0;
        let profile = { pref_captain_id: 'iron', pref_ship_id: 'brigantine' };
        let progLookup = {
            captains: new Map(), ships: new Map(),
        };
        try {
            const [m, f, p, prog] = await Promise.all([
                api.listMatches(),
                api.listFriends(),
                api.getProfile(),
                api.progression(),
            ]);
            matches = m.matches;
            pendingIncoming = f.incoming.length;
            profile = { pref_captain_id: p.pref_captain_id, pref_ship_id: p.pref_ship_id };
            for (const c of prog.captains)
                progLookup.captains.set(c.id, c.name);
            for (const s of prog.ships)
                progLookup.ships.set(s.id, s.name);
        }
        catch (err) {
            if (silent)
                return;
            root.innerHTML = `<div class="error">${err.message}</div>`;
            return;
        }
        const hash = JSON.stringify({
            pi: pendingIncoming,
            sf: showFinished,
            pf: profile,
            m: matches.map((mm) => `${mm.id}:${mm.status}:${mm.is_my_turn}:${mm.is_my_pick}:${mm.invite_code ?? ''}:${mm.won ?? ''}`),
        });
        // Fire "your turn" notifications on every poll — function dedupes per match.
        checkTurnNotifications(matches);
        if (silent && hash === lastHash)
            return;
        lastHash = hash;
        const incomingChallenges = matches.filter((m) => m.status === 'pending_challenge');
        const outgoingChallenges = matches.filter((m) => m.challenge_role === 'outgoing');
        const playable = matches.filter((m) => m.status !== 'finished' && m.status !== 'pending_challenge' && m.challenge_role !== 'outgoing');
        const finished = matches.filter((m) => m.status === 'finished');
        const yourTurn = playable.filter((m) => m.is_my_turn || m.is_my_pick);
        const waiting = playable.filter((m) => !m.is_my_turn && !m.is_my_pick);
        const friendsBadge = pendingIncoming > 0 ? `<span class="nav-badge">${pendingIncoming}</span>` : '';
        let verifyBanner = '';
        if (me.is_guest) {
            verifyBanner = `
        <div class="verify-banner" id="verify-banner">
          <span class="verify-banner-text">
            <strong>⚓ Playing as a guest.</strong>
            Save your unlocks + stats — link this account to a real email so you don't lose your progress when you clear cookies.
          </span>
          <button class="btn btn--primary btn--small" id="btn-save-account">Save account</button>
        </div>`;
        }
        else if (me.email_verified === false) {
            verifyBanner = `
        <div class="verify-banner" id="verify-banner">
          <span class="verify-banner-text">
            <strong>⚠ Verify your email.</strong>
            We sent a link to <strong>${escape(me.email ?? '')}</strong>. Some features stay locked until you confirm it.
          </span>
          <button class="btn btn--ghost btn--small" id="btn-resend-verify">Resend link</button>
        </div>`;
        }
        root.innerHTML = `
      <div class="lobby">
        ${verifyBanner}
        <header class="lobby-header">
          <div class="lobby-brand">
            ${renderLogoMark(40)}
            <h1 class="brand">Plunder</h1>
          </div>
          <nav class="lobby-nav">
            <button id="btn-challenges" class="btn btn--ghost" title="Challenges">⚓ Challenges</button>
            <button id="btn-collection" class="btn btn--ghost" title="Browse all crew, edicts, and deck mods">📚 Collection</button>
            <button id="btn-friends" class="btn btn--ghost" title="Friends">
              <span>Friends</span>${friendsBadge}
            </button>
            <button id="btn-profile" class="btn btn--ghost" title="Profile">Profile</button>
            <button id="btn-notify" class="btn btn--ghost btn--icon" title="Browser notifications">🔔</button>
            <button id="btn-mute" class="btn btn--ghost btn--icon" title="Toggle audio">🔊</button>
            <button id="btn-telemetry" class="btn btn--ghost btn--icon" title="Anonymous gameplay analytics (opt-in)">📊</button>
            <button id="btn-signout" class="btn btn--ghost" title="Sign out">⎋</button>
          </nav>
        </header>

        <button class="loadout-card" id="btn-loadout" type="button" title="Edit profile">
          <div class="loadout-art loadout-art--captain">${captainArt(profile.pref_captain_id)}</div>
          <div class="loadout-art loadout-art--ship">${shipArt(profile.pref_ship_id)}</div>
          <div class="loadout-text">
            <div class="loadout-greet">Sailing as <strong>${escape(me.display_name)}</strong></div>
            <div class="loadout-pick">
              <strong>${escape(progLookup.captains.get(profile.pref_captain_id) ?? profile.pref_captain_id)}</strong>
              · <strong>${escape(progLookup.ships.get(profile.pref_ship_id) ?? profile.pref_ship_id)}</strong>
              <span class="loadout-edit">change in Profile →</span>
            </div>
          </div>
        </button>

        <section class="play-actions">
          <button id="btn-create-pve" class="play-card play-card--solo">
            <div class="play-card-title">⚓ Solo Run</div>
            <div class="play-card-sub">Captain vs the Treasury Galleon</div>
          </button>
          <button id="btn-daily" class="play-card play-card--daily">
            <div class="play-card-title">🌅 Daily Challenge</div>
            <div class="play-card-sub">Same seed for everyone — leaderboard refreshes nightly</div>
          </button>
          <button id="btn-create" class="play-card play-card--pvp">
            <div class="play-card-title">⚔ New PvP Match</div>
            <div class="play-card-sub">Invite a friend with a code</div>
          </button>
          <form id="form-accept" class="play-card play-card--accept">
            <div class="play-card-title">📜 Accept invite</div>
            <div class="play-card-row">
              <input id="invite-input" name="code" placeholder="ABC123" maxlength="16" autocomplete="off" />
              <button type="submit" class="btn">Join</button>
            </div>
          </form>
        </section>

        ${incomingChallenges.length > 0 ? `
          <section class="match-list match-list--challenge">
            <h2>⚔ Incoming challenges</h2>
            ${incomingChallenges.map((m) => `
              <div class="match-row match-row--challenge" data-match-id="${m.id}">
                <div class="match-row-main">
                  <div class="match-row-vs"><strong>${escape(m.opponent_name ?? 'Captain')}</strong> challenges you</div>
                  <div class="muted">Accept uses your current Profile loadout</div>
                </div>
                <div class="match-row-actions">
                  <button class="btn btn--small" data-accept-challenge="${m.id}">Accept</button>
                  <button class="btn btn--small btn--ghost" data-decline-challenge="${m.id}">Decline</button>
                </div>
              </div>`).join('')}
          </section>` : ''}

        ${outgoingChallenges.length > 0 ? `
          <section class="match-list match-list--waiting">
            <h2>Awaiting accept</h2>
            ${outgoingChallenges.map((m) => `
              <div class="match-row" data-match-id="${m.id}">
                <div class="match-row-main">
                  <div class="match-row-vs">vs <strong>${escape(m.opponent_name ?? 'Captain')}</strong> <span class="badge badge--waiting">Pending</span></div>
                </div>
                <button class="match-forfeit" data-decline-challenge="${m.id}" title="Cancel challenge">✕</button>
              </div>`).join('')}
          </section>` : ''}

        ${yourTurn.length > 0 ? `
          <section class="match-list match-list--turn">
            <h2>⏳ Your move</h2>
            ${yourTurn.map(matchRow).join('')}
          </section>` : ''}

        ${waiting.length > 0 ? `
          <section class="match-list match-list--waiting">
            <h2>Waiting on others</h2>
            ${waiting.map(matchRow).join('')}
          </section>` : ''}

        ${(playable.length === 0 && incomingChallenges.length === 0 && outgoingChallenges.length === 0) ? `
          <section class="match-list match-list--empty">
            <div class="empty-state">
              <div class="empty-state-art">⚓</div>
              <div class="empty-state-text">No active voyages. Pick one above to set sail.</div>
            </div>
          </section>` : ''}

        <section class="match-list match-list--past">
          <button class="past-toggle" id="past-toggle">
            <span>Past matches (${finished.length})</span>
            <span class="past-arrow">${showFinished ? '▾' : '▸'}</span>
          </button>
          ${showFinished ? `
            <div class="past-list">
              ${finished.length === 0
            ? '<div class="empty">No history yet.</div>'
            : finished.slice(0, 12).map(matchRow).join('')}
            </div>` : ''}
        </section>
      </div>
    `;
        // Audio mute toggle
        const muteBtn = root.querySelector('#btn-mute');
        if (muteBtn) {
            muteBtn.textContent = mixer.muted ? '🔇' : '🔊';
            muteBtn.addEventListener('click', () => {
                mixer.unlock();
                mixer.setMuted(!mixer.muted);
                muteBtn.textContent = mixer.muted ? '🔇' : '🔊';
            });
        }
        // Notification toggle. Tri-state: unsupported / not-yet-asked / on / off.
        const notifyBtn = root.querySelector('#btn-notify');
        if (notifyBtn) {
            const renderState = () => {
                const perm = notificationsPermission();
                if (perm === 'unsupported' || perm === 'denied') {
                    notifyBtn.textContent = '🔕';
                    notifyBtn.title = perm === 'denied' ? 'Notifications blocked — enable in browser settings' : 'Notifications not supported';
                    notifyBtn.disabled = true;
                    return;
                }
                if (perm === 'default') {
                    notifyBtn.textContent = '🔔';
                    notifyBtn.title = 'Enable turn notifications';
                    return;
                }
                // granted
                notifyBtn.textContent = notificationsEnabled() ? '🔔' : '🔕';
                notifyBtn.title = notificationsEnabled() ? 'Notifications on (click to mute)' : 'Notifications muted (click to enable)';
            };
            renderState();
            notifyBtn.addEventListener('click', async () => {
                const perm = notificationsPermission();
                if (perm === 'default') {
                    await requestNotificationPermission();
                    renderState();
                    return;
                }
                if (perm === 'granted') {
                    setNotificationsEnabled(!notificationsEnabled());
                    renderState();
                }
            });
        }
        const saveAccountBtn = root.querySelector('#btn-save-account');
        saveAccountBtn?.addEventListener('click', () => openPromoteModal(me, () => loadAndRender()));
        const resendBtn = root.querySelector('#btn-resend-verify');
        if (resendBtn) {
            resendBtn.addEventListener('click', async () => {
                resendBtn.disabled = true;
                const original = resendBtn.textContent;
                resendBtn.textContent = 'Sending…';
                try {
                    await api.resendVerification();
                    resendBtn.textContent = 'Sent ✓';
                }
                catch (err) {
                    resendBtn.textContent = err.message.slice(0, 40);
                    setTimeout(() => { resendBtn.disabled = false; resendBtn.textContent = original; }, 3000);
                }
            });
        }
        const telBtn = root.querySelector('#btn-telemetry');
        if (telBtn) {
            const sync = () => {
                const on = isTelemetryEnabled();
                telBtn.classList.toggle('btn--ghost-on', on);
                telBtn.title = on
                    ? 'Anonymous gameplay analytics: ON (click to opt out)'
                    : 'Anonymous gameplay analytics: OFF (click to opt in)';
            };
            sync();
            telBtn.addEventListener('click', () => {
                const next = !isTelemetryEnabled();
                setTelemetryEnabled(next);
                sync();
            });
        }
        root.querySelector('#btn-friends')?.addEventListener('click', goToFriends);
        root.querySelector('#btn-profile')?.addEventListener('click', goToProfile);
        root.querySelector('#btn-challenges')?.addEventListener('click', goToChallenges);
        root.querySelector('#btn-collection')?.addEventListener('click', goToCollection);
        root.querySelector('#btn-loadout')?.addEventListener('click', goToProfile);
        root.querySelector('#btn-signout')?.addEventListener('click', async () => {
            await api.logout();
            signOut();
        });
        root.querySelector('#btn-create')?.addEventListener('click', async () => {
            try {
                const created = await api.createMatch();
                prompt(`Share this invite code with your opponent:\n\n${created.invite_code}\n\nClick OK to enter the match (you're waiting for them to accept).`, created.invite_code);
                goToMatch(created.match_id);
            }
            catch (err) {
                if (await handleVerifyEmailError(err))
                    return;
                alert(err.message);
            }
        });
        root.querySelector('#btn-create-pve')?.addEventListener('click', () => {
            openRunBuilder().catch((err) => alert(err.message));
        });
        root.querySelector('#btn-daily')?.addEventListener('click', async () => {
            try {
                const dc = await api.dailyChallenge();
                if (dc.my_status.played) {
                    alert(`You've already played today's challenge.\n\n` +
                        `Your score: 🪙${dc.my_status.doubloons} (ante ${dc.my_status.ante_reached}).\n` +
                        `Come back tomorrow for a new run.`);
                    return;
                }
                const created = await api.createPveMatch({
                    captain_id: dc.captain_id,
                    ship_id: dc.ship_id,
                    voyage_id: dc.voyage_id,
                    seed: dc.seed,
                });
                goToMatch(created.match_id);
            }
            catch (err) {
                if (await handleVerifyEmailError(err))
                    return;
                alert(err.message);
            }
        });
        root.querySelector('#form-accept')?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const input = root.querySelector('#invite-input');
            const code = input.value.trim().toUpperCase();
            if (!code)
                return;
            try {
                const res = await api.acceptInvite(code);
                goToMatch(res.match_id);
            }
            catch (err) {
                const msg = err instanceof ApiError ? err.message : err.message;
                if (err instanceof ApiError && err.status === 404) {
                    try {
                        const list = await api.listMatches();
                        const recent = list.matches.find((m) => m.status !== 'finished');
                        if (recent) {
                            alert("That invite was already accepted. Opening your active match.");
                            goToMatch(recent.id);
                            return;
                        }
                    }
                    catch { /* fall through */ }
                    alert("That invite code isn't valid (or already used). Ask your friend to share again.");
                    return;
                }
                alert(msg);
            }
        });
        root.querySelector('#past-toggle')?.addEventListener('click', () => {
            showFinished = !showFinished;
            lastHash = ''; // force re-render
            loadAndRender();
        });
        root.querySelectorAll('[data-match-id]').forEach((el) => {
            el.addEventListener('click', (e) => {
                if (e.target.closest('button'))
                    return;
                goToMatch(el.dataset.matchId);
            });
        });
        root.querySelectorAll('button[data-forfeit]').forEach((btn) => {
            btn.addEventListener('click', async (e) => {
                e.stopPropagation();
                const matchId = btn.dataset.forfeit;
                const mode = btn.dataset.mode || 'pvp';
                const msg = mode === 'pve'
                    ? 'End this solo run? It counts as a finished run in your stats.'
                    : 'Forfeit this match? Your opponent wins immediately.';
                if (!confirm(msg))
                    return;
                try {
                    await api.forfeitMatch(matchId);
                    await loadAndRender(true);
                }
                catch (err) {
                    alert(err.message);
                }
            });
        });
        root.querySelectorAll('button[data-accept-challenge]').forEach((btn) => {
            btn.addEventListener('click', async (e) => {
                e.stopPropagation();
                try {
                    const res = await api.acceptChallenge(btn.dataset.acceptChallenge);
                    goToMatch(res.match_id);
                }
                catch (err) {
                    alert(err.message);
                }
            });
        });
        root.querySelectorAll('button[data-decline-challenge]').forEach((btn) => {
            btn.addEventListener('click', async (e) => {
                e.stopPropagation();
                if (!confirm('Decline this challenge? It will be removed.'))
                    return;
                try {
                    await api.declineChallenge(btn.dataset.declineChallenge);
                    await loadAndRender(true);
                }
                catch (err) {
                    alert(err.message);
                }
            });
        });
        root.querySelectorAll('button[data-copy-code]').forEach((btn) => {
            btn.addEventListener('click', async (e) => {
                e.stopPropagation();
                const code = btn.dataset.copyCode;
                try {
                    await navigator.clipboard.writeText(code);
                    btn.textContent = '✓ Copied';
                    setTimeout(() => { btn.textContent = '📋 Copy'; }, 1500);
                }
                catch {
                    prompt('Copy this code:', code);
                }
            });
        });
    }
}
function matchRow(m) {
    let badge = '';
    const modeBadge = m.match_mode === 'pve' ? '<span class="badge badge--pve">Solo</span>' : '';
    if (m.status === 'awaiting_opponent') {
        const code = escape(m.invite_code ?? '');
        badge = `<span class="badge badge--waiting">Awaiting opponent · <span class="invite-code-pill">${code}</span></span>
             <button class="btn btn--small copy-code-btn" data-copy-code="${code}" title="Copy invite code">📋 Copy</button>`;
    }
    else if (m.status === 'in_progress') {
        if (m.match_mode === 'pve') {
            const endlessTag = (m.current_ante ?? 0) > 5 ? ' (endless)' : '';
            const pickPart = m.is_my_pick ? 'Port pick' : `Ante ${m.current_ante}${endlessTag} · 🪙${m.ante_threshold}`;
            badge = `<span class="badge badge--turn">${pickPart}</span>`;
        }
        else if (m.is_my_pick)
            badge = '<span class="badge badge--turn">Your pick at the Port</span>';
        else if (m.is_my_turn)
            badge = '<span class="badge badge--turn">Your turn</span>';
        else
            badge = '<span class="badge badge--wait">Waiting on opponent</span>';
    }
    else if (m.status === 'finished') {
        badge = m.won
            ? '<span class="badge badge--win">Won</span>'
            : '<span class="badge badge--loss">Lost</span>';
    }
    const title = m.match_mode === 'pve' ? 'Solo Run' : `vs ${m.opponent_name ? escape(m.opponent_name) : 'TBD'}`;
    const isActive = m.status === 'in_progress' || m.status === 'awaiting_opponent';
    const forfeitBtn = isActive
        ? `<button class="match-forfeit" data-forfeit="${m.id}" data-mode="${m.match_mode}" title="${m.match_mode === 'pve' ? 'End run' : 'Forfeit match'}">✕</button>`
        : '';
    return `<div class="match-row" data-match-id="${m.id}">
    <div class="match-row-main">
      <div class="match-row-vs">${title} ${modeBadge}</div>
      ${badge}
    </div>
    <div class="match-row-meta">${formatTime(m.created_at)}</div>
    ${forfeitBtn}
  </div>`;
}
function escape(s) {
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return s.replace(/[&<>"']/g, (ch) => map[ch]);
}
function formatTime(unixSec) {
    const d = new Date(unixSec * 1000);
    return d.toLocaleString();
}
function openPromoteModal(me, onSuccess) {
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.innerHTML = `
    <div class="modal modal--narrow">
      <h2 class="modal-title">Save your account</h2>
      <p class="modal-blurb">
        Link this guest run to a real email + password. Your unlocks, stats, and Captain pick all carry over. We'll send a verification link to confirm the email.
      </p>
      <form id="promote-form" class="auth-form">
        <label>Email
          <input id="promote-email" type="email" autocomplete="email" required maxlength="254" />
        </label>
        <label>Password
          <input id="promote-password" type="password" autocomplete="new-password" required minlength="8" maxlength="256" />
        </label>
        <label>Captain name (optional override)
          <input id="promote-name" type="text" autocomplete="username" maxlength="32" placeholder="${escape(me.display_name)}" />
        </label>
        <div class="auth-status" id="promote-status"></div>
        <div class="modal-actions">
          <button type="button" class="btn btn--ghost" id="promote-cancel">Cancel</button>
          <button type="submit" class="btn btn--primary" id="promote-submit">Save account</button>
        </div>
      </form>
    </div>`;
    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    overlay.querySelector('#promote-cancel')?.addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay)
        close(); });
    const form = overlay.querySelector('#promote-form');
    const status = overlay.querySelector('#promote-status');
    const submit = overlay.querySelector('#promote-submit');
    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const email = overlay.querySelector('#promote-email').value.trim();
        const password = overlay.querySelector('#promote-password').value;
        const name = overlay.querySelector('#promote-name').value.trim();
        submit.disabled = true;
        submit.textContent = 'Saving…';
        status.innerHTML = '';
        try {
            await api.promoteGuest(email, password, name || undefined);
            status.innerHTML = `<div class="auth-success">✓ Account saved. Check ${escape(email)} for a verification link.</div>`;
            setTimeout(() => { close(); onSuccess(); }, 1500);
        }
        catch (err) {
            status.innerHTML = `<div class="auth-error">${escape(err.message)}</div>`;
            submit.disabled = false;
            submit.textContent = 'Save account';
        }
    });
    setTimeout(() => overlay.querySelector('#promote-email')?.focus(), 50);
}
/** Returns true if the error was a verify-email gate (handled here);
 *  caller should NOT show its own alert in that case. */
export async function handleVerifyEmailError(err) {
    if (!(err instanceof ApiError) || !err.isVerifyEmail)
        return false;
    const ok = confirm(`${err.cleanMessage}\n\nWe'll send you a fresh verification link. Click OK to send it.`);
    if (!ok)
        return true;
    try {
        await api.resendVerification();
        alert('Sent! Check your inbox (and spam folder).');
    }
    catch (e) {
        alert(`Couldn't send: ${e.message}`);
    }
    return true;
}
